import { useMutation, useQueryClient, type QueryKey } from '@tanstack/react-query';
import { METHOD, sendJSONRequest } from '@/state/http-request.helpers.ts';
import { useAuth } from '@clerk/clerk-react';
import type { UpdateFlowDto } from '@/types/dtos/flows/request/update-flow.dto.ts';
import { GET_EVENT_FLOWS_QUERY_KEY } from '@/state/queries/flows/use-get-event-flows';
import type { FlowDto } from '@/types/dtos/flows/response/flow.dto';
import { GET_EVENT_QUERY_KEY } from '@/state/queries/events/use-get-event';
import { GET_FLOW_QUERY_KEY } from '@/state/queries/flows/use-get-flow';
import { invalidateSimulationCueSheet } from '@/state/queries/player/use-get-simulation-cue-sheet';
import { invalidatePublicationStateQueries } from '@/state/queries/events/use-publication-state';
import { isFlowHttpMutationsEnabled } from '@/config/flow-mutations';
import {
  buildApplySyncBodyFromFlowDocument,
  postApplyFlowMutationWithConflictRetry,
} from '@/state/mutations/flows/apply-flow-mutation.http';

export type UpdateFlowMutationVariables = UpdateFlowDto & { flowQueryKey?: QueryKey };

export const useUpdateFlowMutation = () => {
  const { getToken } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (variables: UpdateFlowMutationVariables) => {
      const { flowQueryKey, ...updateFlowDto } = variables;
      const token = await getToken();
      if (!token) {
        throw new Error('Not authenticated');
      }
      if (isFlowHttpMutationsEnabled()) {
        return postApplyFlowMutationWithConflictRetry({
          flowId: updateFlowDto.id,
          token,
          queryClient,
          flowQueryKey,
          buildBody: (serverFlow) => {
            const merged = { ...updateFlowDto, version: serverFlow?.version ?? updateFlowDto.version };
            return buildApplySyncBodyFromFlowDocument(merged as Record<string, unknown>);
          },
        });
      }
      return updateFlow(updateFlowDto, token);
    },
    onSuccess: (updatedFlow) => {
      return Promise.all([
        queryClient.invalidateQueries({
          queryKey: [GET_EVENT_FLOWS_QUERY_KEY],
        }),
        queryClient.invalidateQueries({
          queryKey: [GET_EVENT_QUERY_KEY],
        }),
        queryClient.invalidateQueries({
          queryKey: [GET_FLOW_QUERY_KEY, updatedFlow.id],
        }),
        invalidateSimulationCueSheet(queryClient),
        invalidatePublicationStateQueries(queryClient),
      ]);
    },
  });
};

const updateFlow = async (updateFlowDto: UpdateFlowDto, token: string): Promise<FlowDto> => {
  const url = `${import.meta.env.VITE_API_URL}/flows/${updateFlowDto.id}`;
  return sendJSONRequest({ url, method: METHOD.PATCH, body: updateFlowDto, token });
};

export default useUpdateFlowMutation;
